import _ from "lodash";
import React, {
  useEffect,
  useMemo,
  useRef,
  isValidElement,
  cloneElement,
  useState,
} from "react";
import PropTypes from "prop-types";

// Components
import withLoader from "UIComponents/GqlWithLoader";
import I18nHOC from "UIComponents/I18nHOC";
import UIModal from "UIComponents/UIModal";
import AssessmentIcon from "UIComponents/AssessmentIcon";
import FieldComponent from "UnitPlans/components/IBcomponents/FieldComponent/FieldComponent";
import LabelComponent from "UnitPlans/components/LabelComponent";
import FullScreenLoader from "UIComponents/FullScreenLoader";
import StepHeader from "AppComponents/StepHeader";
import Worksheet from "Worksheet";

// DS
import {
  CloseOutlined,
  CollapseArrowsTwoOutlined,
  ExpandArrowsTwoOutlined,
  FolderOutlined,
  TickLargeOutlined,
  LinkOutlined,
} from "@toddle-edu/ds-icons";
import { IconButton, EmptyState, Tooltip } from "@toddle-edu/ds-web";
import {
  fontStyles,
  ProjectOverviewIllustration,
  NoAssignmentIllustration,
} from "@toddle-edu/ds-theme";

// Utils
import { isUserTypeStaff, getArchivedText } from "Utils";
import { GetLESteps } from "Utils/GetLESteps";
import { goToRelativeRoute } from "modules/Services";
import { getElementValue } from "modules/ElementValueUtils";
import {
  formatDueDate,
  formatDueOnDate,
  getIsCustomWeights,
  getIsCustomWeightsV2,
  getIsEnabledLTITools,
} from "ClassRoom/modules/utils";
import {
  updateStepList,
  updateAssessmentTemplate,
  getUnitPlanFieldsFromAssessment,
  combineUnitPlanMultiFieldData,
  updateTemplateForLearningGoals,
  updateCustomLabelsForUBDLearningGoals,
  updateDynamicLabelsForPlannerElements,
} from "Assessments/modules/Utils";
import ACLStore from "lib/aclStore";
import {
  getAssignmentCommonSettings,
  isTurnitinEnabledForGrade,
} from "ClassRoom/components/AddAssignment/AssignmentSettings/Utils";
import { getFormattedUserName } from "Utils/GetFormattedUserName";
import { copyToClipboard } from "Utils/CopyToClipboard";
import { getSelectedLearningCourseId } from "LearningCourse/modules/LearningCourseUtils";

// third party
import { connect } from "react-redux";
import { graphql, compose } from "lib/apolloClient";
import classNames from "classnames";
import withDerivedProps from "lib/withDerivedProps";

// Constants
import { fontStyle, colors } from "Constants";
import {
  ASSESSMENT_TYPE_QUICK_TASK,
  TASK_TYPE_WORKSHEET,
  LE_LIBRARY_LE_SELECTION,
  SELECT_POST,
} from "Constants/stringConstants";
import { USE_NEW_GRADING_SETUP_IN_COURSE_PERM } from "Constants/permissionConstants";
import {
  ASSESSMENT_TYPE_WORKSHEET,
  MODE_ASSESSMENT_VIEW,
  WORKSHEET_VIEW_STEPS,
} from "Worksheet/modules/WorksheetConstants";

// Modules
import { getAssessmentPreviewQuery } from "Assessments/modules/AssessmentQuery";
import { getAssessmentPreviewDetailsFromCache } from "Assessments/modules/AssessmentGraphqlHelpers";
import { getPlannerTemplateFromCache } from "modules/CommonGraphqlHelpers";
import { getPlannerTemplateQuery } from "modules/CommonQuery";
import {
  getIsConfigurationEnabledForCPAY,
  getSettingValue,
} from "modules/PermissionModule";
import { getAssignmentFoldersAndPostSettingsQuery } from "Teacher/routes/Courses/routes/Course/routes/ClassRoom/modules/ClassRoomQuery";
import { getAssignmentFoldersAndPostSettingsFromCache } from "Teacher/routes/Courses/routes/Course/routes/ClassRoom/modules/ClassRoomGraphqlHelpers";
import { getLocalisedTemplate } from "UnitPlans/routes/UnitPlanDetails/routes/EditUnitPlan/routes/IBPlanner/modules/IBPlannerModule";
import { getCurrentAssessmentTypes } from "Platform/modules/PlatformModule";
import { goToBack } from "modules/NavigationModule";
import {
  getSelectedCurriculumId,
  getSelectedAcademicYearId,
  getUserArchivalStatus,
  getUserType,
} from "modules/CommonSelectors";
import {
  setShouldNavigateToFirstStudentAssignment,
  createShortUrl,
  updateStudentAssignmentLinkShortUrlsMapping,
} from "ClassRoom/modules/ClassRoomModule";
import { getSubFields } from "UnitPlans/components/LibraryCommonComponents/ResourceEdit/modules/Utils";
import { setToastMsg } from "Login/modules/LoginModule/LoginActions";
import { getIsLearningCoursesEnabled } from "LearningCourses/modules/Utils";

// Scss
import classes from "./CompactLEPreview.scss";

const FIELD_SPECIFIC_STYLES = {
  goals: {
    customContentEditableStyle: {
      color: colors.textSubtle,
      fontSize: "1.4rem",
    },
  },
  measureAssessing: {
    labelContainerStyle: {
      marginBottom: "8px",
    },
  },
};

const workbookTextStyle = {
  color: "var(--text-primary)",
  fontSize: "1.4rem",
};

const styleObj = {
  assessmentToolsConStyle: {
    maxWidth: "575px",
    overflowX: "hidden",
  },
  textViewcustomTextStyle: {
    ...fontStyle.medium,
    color: colors.textSubtle,
    fontSize: "1.4rem",
    lineHeight: "2rem",
  },
  contentStyle: {
    ...fontStyle.medium,
    color: colors.textSubtle,
    fontSize: "1.4rem",
    lineHeight: "2rem",
  },
  labelContainerStyle: {
    marginBottom: 8,
    width: "fit-content",
  },

  labelStyle: {
    ...fontStyle.demiBold,
    fontSize: "1.6rem",
    lineHeight: "2.4rem",
    color: colors.textDefault,
  },
  labelStyleWorkbook: {
    fontSize: "1.4rem",
  },
  noDisplay: {
    display: "none",
  },
  emptyStateStyles: {
    color: colors.textSubtle,
    fontWeight: fontStyles.regular,
    fontSize: "1.6rem",
    lineHeight: "2.4rem",
    padding: "0 34px",
  },
  workbookModeListStyles: {
    customContentEditableStyle: workbookTextStyle,
  },
  workbookTextViewStyle: workbookTextStyle,
};

const fieldComponentStyles = {
  customMediaCardContainerStyle: { gap: "12px" },
  iconStyles: { maxWidth: "16px" },

  labelIconContainerStyle: {
    width: "fit-content",
  },
};

const BasicDetails = props => {
  const { t, label, value, fieldKey, assessmentDetails } = props;

  const fieldValue = _.isArray(value) ? _.join(value, ", ") : value;

  const { academicTerm, category } = assessmentDetails || {};

  let fields = [
    {
      label,
      value: fieldValue,
    },
  ];

  if (fieldKey === "assessmentCategory" && (academicTerm || category)) {
    if (academicTerm) {
      fields.push({
        label: t("common:term"),
        value: academicTerm?.label,
      });
    }

    if (category) {
      fields.push({
        label: t("common:category"),
        value: category?.label,
      });
    }
  }

  fields = _.filter(fields, field => !_.isEmpty(field?.value));

  if (_.isEmpty(fields)) return null;

  return _.map(fields, field => (
    <div className={classes.basicDetailsContainer}>
      <div className={classes.stepLabel}>{field.label}</div>
      <div className={classes.subtleText}>{field.value}</div>
    </div>
  ));
};

const Header = React.memo(props => {
  const {
    t,
    LETitle,
    createdBy,
    showCreatedBy,
    onCloseClick,
    showCloseButton,
    showFullScreenButton,
    toggleFullscreenLE,
    isFullScreen,
    isFullScreenDisabled,
    assignmentBasicDetails,
    showAssignmentActionsButton,
    assignmentData,
    courseData,
    goToRelativeRoute,
    isMediaManagerRoute,
    totalNewSnpCycles,
    isClassStreamManagerRoute,
    courseId,
    setShouldNavigateToFirstStudentAssignment,
    assessmentType,
    isLinksRoute,
    isWorkbookMode,
    coverImage,
    canPerformActionsInAssignment,
    customHeader,
    isGradebookRoute,
    AssignmentActionsDropdown,
    assignmentId,
    createShortUrl,
    updateStudentAssignmentLinkShortUrlsMapping,
    studentAssignmentLinkShortUrlsMapping,
    setToastMsg,
    curriculumProgramId,
    showAssignmentCopyLinkOption,
    learningCourseId,
    isClassroomRoute,
    isClassflowRoute,
    onOrganizationResourceDelete,
    upperContainerStyle,
    titleStyle,
  } = props;

  const [isLoading, setIsLoading] = useState(false);

  const isLearningCoursesEnabled = getIsLearningCoursesEnabled();

  const isStaffArchived = getUserArchivalStatus(createdBy);
  const formattedUserName = getFormattedUserName({
    user: createdBy,
    type: "staff",
  });
  const formattedCreatedBy = getArchivedText({
    name: formattedUserName,
    isArchived: isStaffArchived,
    t,
  });

  const subtext = showCreatedBy
    ? t("common:by_with_label", {
        label: formattedCreatedBy,
      })
    : formatDueDate({
        deadline: _.get(assignmentBasicDetails, "deadline"),
        t,
      });

  /** Remove comma and colon from the label */
  const subtextWorkbook = formatDueOnDate({
    deadline: _.get(assignmentBasicDetails, "deadline"),
    t,
  });

  const subTextLabel = isWorkbookMode ? subtextWorkbook : subtext;

  const headerRightContainerClass = classNames({
    [classes.headerRightContainer]: true,
    [classes.fullScreenHeaderRightContainer]: isFullScreen,
  });

  const upperConClass = classNames({
    [classes.upperCon]: true,
    [classes.alignStart]: isWorkbookMode,
    [classes.fullScreenUpperCon]: isFullScreen,
  });

  const upperConWrapper = classNames({
    [classes.upperContainerWrapper]: !isFullScreen,
    [classes.upperConWorkbook]: isWorkbookMode,
  });

  const leTitleClass = classNames({
    [classes.leTitle]: !isWorkbookMode,
    [classes.leTitleWorkbook]: isWorkbookMode,
    ["text-label"]: !isWorkbookMode,
    ["text-label-l"]: isWorkbookMode,
  });

  const coverImageWorkbookClass = classNames({
    [classes.coverImageWorkbook]: isWorkbookMode,
  });

  const titleContainerClass = classNames({
    [classes.flexColumn]: true,
    [classes.marginVerticalAuto]: isWorkbookMode,
  });

  const handleCopyLink = async () => {
    //check if short url already exists in mapping (redux)

    let shortUrl = "";
    const mappedShortUrl = _.get(
      studentAssignmentLinkShortUrlsMapping,
      assignmentId,
      null
    );
    if (mappedShortUrl) {
      shortUrl = mappedShortUrl;
    } else {
      //extract url till ".../platform" route
      const url = window.location.href;
      const platformUrl = url.substring(0, url.indexOf("/platform") + 9);

      let urlToCopy = "";

      //final student assignment url
      if (isLearningCoursesEnabled && learningCourseId) {
        urlToCopy = `${platformUrl}/${curriculumProgramId}/learning-course/${learningCourseId}/class/${courseId}/classroom/classroom-details/${assignmentId}`;
      } else {
        urlToCopy = `${platformUrl}/${curriculumProgramId}/courses/${courseId}/classroom/classroom-details/${assignmentId}`;
      }

      //create short url and copy to clipboard
      setIsLoading(true);
      shortUrl = await createShortUrl({
        input: { target: urlToCopy },
      });
      setIsLoading(false);
    }

    if (shortUrl) {
      copyToClipboard(shortUrl, () => {
        setToastMsg({
          msg: "toastMsgs:Successfully_copied_clipboard",
          type: "success",
        });

        //update mapping in redux
        updateStudentAssignmentLinkShortUrlsMapping({
          [assignmentId]: shortUrl,
        });
      });
    }
  };

  const onActionDone = useMemo(() => {
    const onDelete = () => {
      if (isGradebookRoute) {
        return;
      } else if (isClassroomRoute) {
        onCloseClick();
        return;
      } else if (isClassflowRoute) {
        onOrganizationResourceDelete?.();
        return;
      } else if (isLinksRoute) {
        const route = isMediaManagerRoute
          ? "../../../../../.."
          : "../../../../..";
        goToRelativeRoute({ route, type: "replace" });
      } else {
        const route = isMediaManagerRoute ? "../../../.." : "../../..";
        goToRelativeRoute({ route, type: "replace" });
      }
    };

    const onEdit = () => {
      setShouldNavigateToFirstStudentAssignment(true);
    };

    return {
      DELETE: onDelete,
      delete: onDelete,
      EDIT: onEdit,
      edit: onEdit,
      REASSIGN: onEdit,
    };
  }, [
    isLinksRoute,
    isMediaManagerRoute,
    isClassroomRoute,
    isClassflowRoute,
    isGradebookRoute,
    onOrganizationResourceDelete,
  ]);

  if (isValidElement(customHeader)) {
    return cloneElement(customHeader, {
      assessmentTitle: LETitle,
      deadline: _.get(assignmentBasicDetails, "deadline"),
    });
  }

  return (
    <div className={upperConWrapper} style={upperContainerStyle}>
      <div className={upperConClass}>
        <div className={classes.leftContainer}>
          <AssessmentIcon
            src={coverImage}
            className={coverImageWorkbookClass}
            assessmentType={assessmentType}
          />
          <div className={titleContainerClass}>
            <div className={leTitleClass} style={titleStyle}>
              {LETitle}
            </div>
            <div className={classes.subtext}>{subTextLabel}</div>
          </div>
        </div>
        <div className={headerRightContainerClass}>
          {showAssignmentCopyLinkOption ? (
            <Tooltip
              tooltip={t("classRoom:copy_assignment_link_for_student")}
              showArrow={true}
              dsVersion="2.0"
            >
              <IconButton
                icon={<LinkOutlined variant="subtle" />}
                size={"medium"}
                variant={"on"}
                testId="instructionsPanel-copy-link"
                onClick={handleCopyLink}
              />
            </Tooltip>
          ) : null}
          {showFullScreenButton && !isFullScreenDisabled && !isWorkbookMode && (
            <div className={classes.iconContainer} onClick={toggleFullscreenLE}>
              {isFullScreen ? (
                <IconButton
                  icon={<CollapseArrowsTwoOutlined variant="subtle" />}
                  size={"medium"}
                  variant={"on"}
                  testId="instructionsPanel-collapse"
                />
              ) : (
                <IconButton
                  icon={<ExpandArrowsTwoOutlined variant="subtle" />}
                  size={"medium"}
                  variant={"on"}
                  testId="instructionsPanel-expand"
                />
              )}
            </div>
          )}
          {showCloseButton ? (
            <IconButton
              icon={<CloseOutlined />}
              type="plain"
              variant="neutral"
              onClick={onCloseClick}
              size="medium"
              testId="instructionsPanel-close"
              dsVersion="2.0"
            />
          ) : null}
          {isFullScreen &&
          showAssignmentActionsButton &&
          !isWorkbookMode &&
          canPerformActionsInAssignment ? (
            <>
              <div className={classes.seperator}></div>
              <AssignmentActionsDropdown
                assignment={assignmentData}
                course={courseData}
                courseId={courseId}
                totalNewSnpCycles={totalNewSnpCycles}
                creationSource={
                  isGradebookRoute
                    ? "GRADEBOOK"
                    : isClassStreamManagerRoute
                    ? "CLASS_STREAM_REVIEW_DETAILS"
                    : "CLASSROOM_DETAILS"
                }
                onActionDone={onActionDone}
                viewType={"instructions"}
                dsVersion="2.0"
                buttonType={"plain"}
                hideViewInstructions={true}
              />
            </>
          ) : null}
        </div>
        {isLoading ? <FullScreenLoader /> : null}
      </div>
    </div>
  );
});

Header.displayName = "Header";

const ZERO_PADDING_VIEW_CONTAINER_STYLE = { padding: 0 };

const Fields = React.memo(props => {
  const {
    t,
    fields,
    stepKey,
    stepLabel,
    field_list,
    assessmentFields,
    unitPlanFields,
    assessmentId,
    portalType,
    attachmentItemHeight,
    assessmentDetails,
    isBasicDetailsStep,
    isStudentWorksheetWebView,
    currentAssessmentTypes,
    courseId,
    isWorkbookMode,
    omitDescriptionLabel,
    customTextViewStyle = {},
    isFromAITutorAwards,
  } = props;

  const skipFieldStyle =
    isFromAITutorAwards && _.isEqual(stepKey, "chatbotSettings");

  return _.map(fields, fieldKey => {
    const field = field_list[fieldKey] ? field_list[fieldKey] : {};
    const {
      viewType,
      config: {
        viewLabel,
        label,
        styles,
        options,
        fieldTypeConfig = {},
        plannerElementConfig = {},
      } = {},
    } = field;
    const fieldData = _.find(assessmentFields, { uid: fieldKey });
    const unitPlanFieldValue = _.get(
      _.find(unitPlanFields, { uid: fieldKey }),
      "value",
      ""
    );

    const resolvedValue = _.get(fieldData, "resolvedMinimalTree", "") || {};
    let subjectIds = [];
    if (
      _.includes(
        [
          "benchmarks",
          "learningStandardUBD",
          "learningStandardMYP",
          "subjectStandardDP",
        ],
        fieldKey
      )
    ) {
      subjectIds = _.get(
        _.find(unitPlanFields, { uid: "subjects" }),
        "value",
        ""
      );
    }

    const assessmentFieldValue = _.get(fieldData, "value", "");

    let value = getElementValue({
      fieldUID: fieldKey,
      valueKeys: assessmentFieldValue,
      unitPlanFieldValue,
      fieldObj: field,
      subjectIds: subjectIds,
      resolvedValue: resolvedValue,
      t,
      currentAssessmentTypes,
    });

    let isDownloadEnabled = true;

    if (fieldKey === "openingMessageResource") {
      value = _.map(value || [], group => ({
        ...group,
        attachments: _.map(group?.attachments || [], att => {
          const { type, metadata } = att || {};

          const isLinkWithCheckPoints =
            _.isEqual(type, "LINK") && _.has(metadata, "checkPoints");

          return {
            ...att,
            type: isLinkWithCheckPoints ? "VIDEO" : att.type,
          };
        }),
      }));
      isDownloadEnabled = false;
    }

    if (isBasicDetailsStep) {
      return (
        <BasicDetails
          t={t}
          fieldKey={fieldKey}
          label={viewLabel || label}
          value={value}
          assessmentDetails={assessmentDetails}
        />
      );
    }

    const style = {
      ..._.get(styles, "style", {}),
      ..._.get(styles, "viewStyle", {}),
    };

    const assessmentTools = _.get(assessmentDetails, "assessmentTools");
    const weightage = _.get(assessmentDetails, "weightage");

    const audioRecorderSelectorProps = isWorkbookMode
      ? { version: "v5", showWaveform: true, showAudioDownloadButton: false }
      : {};

    const fieldSpecificStyles = isWorkbookMode
      ? styleObj.workbookModeListStyles
      : { ...(FIELD_SPECIFIC_STYLES[fieldKey] || {}) };

    const textViewCustomTextStyle =
      customTextViewStyle ||
      (isWorkbookMode
        ? styleObj.workbookModeListStyles
        : styleObj.textViewcustomTextStyle);

    const labelStyleWorkbook =
      omitDescriptionLabel && _.isEqual(fieldKey, "description")
        ? styleObj.noDisplay
        : styleObj.labelStyleWorkbook;

    const fieldComponentClasses = classNames({
      [classes.item]: true,
      [classes.audioBottom]:
        isWorkbookMode && _.isEqual(fieldKey, "voiceInstruction"),
    });

    const effectiveFieldTypeConfig = skipFieldStyle
      ? _.omit(fieldTypeConfig, "containerStyle")
      : fieldTypeConfig;

    return (
      <div key={fieldKey} className={fieldComponentClasses} style={style}>
        <FieldComponent
          {...effectiveFieldTypeConfig}
          defaultOptions={options || []}
          textViewcustomTextStyle={textViewCustomTextStyle}
          isApplyTextViewcustomTextStyle={true}
          shouldInheritFontSize={true}
          contentStyle={styleObj.contentStyle}
          {...(skipFieldStyle && {
            viewContainerStyle: ZERO_PADDING_VIEW_CONTAINER_STYLE,
          })}
          styles={{
            labelContainerStyle: {
              marginBottom: viewLabel || label ? "8px" : "0px",
              width: "fit-content",
            },
          }}
          studentTemplateCardStyle={styleObj.studentTemplateCardStyle}
          resourceContainerStyle={styleObj.resourceContainerStyle}
          attachments={_.get(fieldData, "attachments", [])}
          type={viewType}
          mode={"view"}
          parentType={"ASSESSMENT"}
          parentId={assessmentId}
          label={viewLabel || label}
          labelComponent={
            <LabelComponent
              label={viewLabel || label}
              labelStyle={{
                ...fontStyle.demiBold,
                fontSize: "1.6rem",
                lineHeight: "2.4rem",
                color: colors.surfaceDark,
                ...labelStyleWorkbook,
              }}
              labelIconContainerStyle={
                fieldComponentStyles.labelIconContainerStyle
              }
            />
          }
          value={value}
          fieldId={_.get(fieldData, "id", "")}
          fieldKey={fieldKey}
          portalType={portalType}
          attachmentItemHeight={attachmentItemHeight}
          isAssessmentToolPreview={true}
          assessmentToolsConStyle={styleObj.assessmentToolsConStyle}
          isAssessmentCreationRevamp={true}
          assessmentTools={assessmentTools}
          field_list={field_list}
          assessmentFields={assessmentFields}
          unitPlanFields={unitPlanFields}
          stepLabel={stepLabel}
          isInstructionsRevamp={true}
          showToolPrintOption={!isStudentWorksheetWebView}
          isLearningGoalsEvaluationEnabled={_.get(
            assessmentDetails,
            "isLearningGoalsEvaluationEnabled",
            false
          )}
          {...fieldComponentStyles}
          {...fieldSpecificStyles}
          {...audioRecorderSelectorProps}
          plannerElementConfig={plannerElementConfig}
          weightage={weightage}
          subFields={getSubFields({
            fieldKey,
            fieldObj: field_list[fieldKey],
            unitPlanFields,
          })}
          selectedCourseId={courseId}
          isWorkbookMode={isWorkbookMode}
          dsVersion={"2.0"}
          isDownloadEnabled={isDownloadEnabled}
        />
      </div>
    );
  });
});

Fields.displayName = "Fields";

const swapPositions = (fields, index1, index2) => {
  if (_.isEqual(index1, -1) || _.isEqual(index2, -1)) return fields;

  const temp = fields[index1];
  fields[index1] = fields[index2];
  fields[index2] = temp;
  return fields;
};

const Steps = React.memo(props => {
  const {
    view_steps,
    step_list,
    field_list,
    assessmentFields,
    unitPlanFields,
    unitPlanAllFields,
    assessmentId,
    portalType,
    attachmentItemHeight,
    t,
    assessmentDetails,
    isFullScreenLE,
    isStudentWorksheetWebView,
    currentAssessmentTypes,
    courseId,
    isWorkbookMode,
    filterViewSteps = [],
    filterViewFields = [],
    customTextViewStyle = {},
    showPostSettings,
    isFromAITutorAwards,
  } = props;

  const filteredViewSteps = !_.isEmpty(filterViewSteps)
    ? _.filter(view_steps, stepKey => !_.includes(filterViewSteps, stepKey))
    : view_steps;

  return _.map(filteredViewSteps, (key, stepIndex) => {
    const { label } = step_list[key] || {};
    let { fields } = step_list[key] || {};
    const filterArray = ["image", "title"];

    if (isWorkbookMode) {
      filterArray.push("studentTemplate", "plannerResources");
    }

    // Add filterViewFields items to filterArray if not empty
    if (!_.isEmpty(filterViewFields)) {
      filterArray.push(...filterViewFields);
    }

    fields = _.filter(fields, field => !_.includes(filterArray, field));

    if (_.isEmpty(fields)) return null;

    /** Note: Temporary logic until both Description and VoiceInstructions are combined. Till then,
     * we combine both of them in the frontend. And their positions in the fields array will be swapped. */
    let omitDescriptionLabel = false;
    if (isWorkbookMode && !_.isEmpty(fields)) {
      const descriptionText = t("common:description");
      if (_.includes(fields, "voiceInstruction")) {
        _.set(field_list, "voiceInstruction.config.label", descriptionText);
        _.set(field_list, "voiceInstruction.config.viewLabel", descriptionText);
        omitDescriptionLabel = true;
      } else if (_.includes(fields, "description")) {
        _.set(field_list, "description.config.label", descriptionText);
        _.set(field_list, "description.config.viewLabel", descriptionText);
      }
      swapPositions(
        fields,
        _.indexOf(fields, "description"),
        _.indexOf(fields, "voiceInstruction")
      );
    }

    const isBasicDetailsStep = _.includes(["basicV2", "s1"], key);

    const fieldsContainerClass = classNames({
      [classes.formContainer]: true,
      [classes.basicDetailsFormContainer]: isBasicDetailsStep,
    });

    const separatorClass = classNames({
      [classes.separator]: true,
      [classes.hideSeparator]: isWorkbookMode,
    });

    const isLastStep = stepIndex === _.size(filteredViewSteps) - 1;

    return (
      <div key={key} className={classes.stepContainer}>
        <div className={fieldsContainerClass}>
          <Fields
            t={t}
            fields={fields}
            stepKey={key}
            stepLabel={label}
            field_list={field_list}
            assessmentFields={assessmentFields}
            unitPlanFields={unitPlanFields}
            unitPlanAllFields={unitPlanAllFields}
            assessmentId={assessmentId}
            portalType={portalType}
            attachmentItemHeight={attachmentItemHeight}
            assessmentDetails={assessmentDetails}
            isFullScreenLE={isFullScreenLE}
            isBasicDetailsStep={isBasicDetailsStep}
            isStudentWorksheetWebView={isStudentWorksheetWebView}
            currentAssessmentTypes={currentAssessmentTypes}
            courseId={courseId}
            isWorkbookMode={isWorkbookMode}
            omitDescriptionLabel={omitDescriptionLabel}
            filterViewSteps={filterViewSteps}
            customTextViewStyle={customTextViewStyle}
            isFromAITutorAwards={isFromAITutorAwards}
          />
        </div>
        {(showPostSettings || !isLastStep) && (
          <div className={separatorClass} />
        )}
      </div>
    );
  });
});

Steps.displayName = "Steps";

/**
 * @description - Temporary function to customize view for workbook mode and display the instructions.
 * @returns {Array} - Returns the view steps based on the mode.
 */
const getViewStepsHelper = ({ isWorkbookMode, view_steps, step_list }) => {
  if (isWorkbookMode) {
    const removeStudentTemplate = view_step => {
      /** If only student template is present, return empty */
      const viewSteps = _.get(step_list, `${view_step}.fields`, []);
      const firstStep = _.get(viewSteps, "[0]", "");
      if (
        _.isEqual(firstStep, "studentTemplate") &&
        _.isEqual(_.size(viewSteps), 1)
      ) {
        return [];
      }
      return [view_step];
    };
    if (_.includes(view_steps, "implementationV2"))
      return removeStudentTemplate("implementationV2");
    if (_.includes(view_steps, "implementation"))
      return removeStudentTemplate("implementation");
    return [];
  }

  return view_steps;
};

const CompactLEPreview = React.memo(props => {
  const {
    step_list,
    view_steps,
    field_list,
    assessmentDetails,
    assessmentFields,
    unitPlanFields,
    unitPlanAllFields,
    assessmentId,
    portalType,
    attachmentItemHeight,
    t,
    showCloseButton = false,
    onCloseClick,
    wrapperStyle,
    toggleFullscreenLE,
    isFullScreenLE,
    showFullScreenButton,
    showEmptyState,
    customFullScreenUpperCon,
    assignmentBasicDetails,
    isTurnitinEnabledForGrade,
    curriculumType,
    assessmentType,
    getCurrentAssessmentTypes,
    showAssignmentActionsButton = false,
    assignmentData,
    courseData,
    goToRelativeRoute,
    isMediaManagerRoute,
    totalNewSnpCycles,
    isDraftOrScheduledView,
    isClassStreamManagerRoute,
    studentAssignmentId,
    showPostSettings,
    showCreatedBy,
    goToBack,
    courseId,
    userType,
    setShouldNavigateToFirstStudentAssignment,
    isStudentWorksheetWebView,
    isLinksRoute,
    isWorkbookMode,
    canPerformActionsInAssignment = true,
    customHeader,
    filterViewSteps,
    filterViewFields,
    lowerConWrapperStyle = {},
    customTextViewStyle = {},
    isGradebookRoute,
    AssignmentActionsDropdown,
    assignmentId,
    createShortUrl,
    updateStudentAssignmentLinkShortUrlsMapping,
    studentAssignmentLinkShortUrlsMapping,
    setToastMsg,
    curriculumProgramId,
    showAssignmentCopyLinkOption = false,
    currentStep,
    learningCourseId,
    taskTypeGroup,
    isClassroomRoute,
    isClassflowRoute,
    onOrganizationResourceDelete,
    upperContainerStyle,
    titleStyle,
    isFromAITutorAwards,
  } = props;

  const currentAssessmentTypes = getCurrentAssessmentTypes();
  const containerRef = useRef(null);
  const isWorksheet =
    _.isEqual(assessmentType, ASSESSMENT_TYPE_WORKSHEET) ||
    _.isEqual(taskTypeGroup, TASK_TYPE_WORKSHEET);
  const [currentWorksheetViewStep, setCurrentWorksheetViewStep] = useState(
    "ASSESSMENT_DETAILS"
  );
  const onClickWorksheetStep = value => {
    setCurrentWorksheetViewStep(value);
  };

  // Ensure that this component receives focus when it is mounted,
  // particularly when navigating via the Tab key,
  useEffect(() => {
    setTimeout(() => {
      containerRef.current?.focus();
    }, 300);
  }, []);

  const {
    title: { value: LETitleRaw },
    createdBy,
  } = assessmentDetails;
  const LETitle = LETitleRaw || t("common:untitled");

  const showStepHeader =
    !_.includes([LE_LIBRARY_LE_SELECTION, SELECT_POST], currentStep) &&
    isFullScreenLE;

  const coverImage = _.get(assessmentDetails, "image.value", "");

  const containerClass = classNames({
    [classes.container]: true,
    [classes.fullScreenContainer]: isFullScreenLE,
  });

  const wrapper = classNames({
    [classes.wrapper]: true,
    [classes.fullScreenWrapper]: isFullScreenLE,
  });

  const lowerConClass = classNames({
    [classes.lowerCon]: true,
    [classes.fullScreenLowerCon]: isFullScreenLE,
  });

  const isQt = _.isEqual(
    _.get(assessmentDetails, "type"),
    ASSESSMENT_TYPE_QUICK_TASK
  );

  const settingItems = getAssignmentCommonSettings({
    t,
    isQt,
    isTurnitinEnabledForGrade,
    ...(assignmentBasicDetails || {}),
    isExcludedFromFinalGradeCalculationEnabled: ACLStore.can(
      "FeatureFlag:isExcludedFromFinalGradeCalculationEnabled"
    ),
    isIncludedInCourseFlow: _.get(
      assignmentBasicDetails,
      "content.isIncludedInCourseFlow",
      false
    ),
  });

  const folders = _.get(assignmentBasicDetails, "folders", []);
  const viewStepsArray = getViewStepsHelper({
    isWorkbookMode,
    view_steps,
    step_list,
  });
  const isViewStepsEmpty = _.isEmpty(viewStepsArray);
  const emptyIllustration = isWorkbookMode
    ? NoAssignmentIllustration
    : ProjectOverviewIllustration;
  const emptyTitle = isWorkbookMode
    ? t("common:no_instructions")
    : t("common:empty_instructions_title_text");
  const emptySubtitle = isWorkbookMode
    ? t("common:no_instructions_subtitle")
    : t("common:empty_instructions_subtitle_text");

  const lowerConClassWrapper = classNames({
    [classes.lowerConWrapper]: true,
    [classes.lowerConFullScreenWrapper]: isFullScreenLE,
    [classes.lowerConWorkbookWrapper]: isWorkbookMode,
    [classes.lowerConEmptyWrapper]: isWorkbookMode && isViewStepsEmpty,
  });

  const lePreviewComponent = (
    <div
      className={containerClass}
      onClick={e => e?.stopPropagation()}
      ref={containerRef}
      tabIndex={-1}
    >
      <div className={wrapper} style={wrapperStyle}>
        {!isDraftOrScheduledView ? (
          <Header
            t={t}
            LETitle={LETitle}
            createdBy={createdBy}
            onCloseClick={onCloseClick}
            showCloseButton={showCloseButton}
            showCreatedBy={showCreatedBy}
            toggleFullscreenLE={toggleFullscreenLE}
            isFullScreen={isFullScreenLE}
            showFullScreenButton={showFullScreenButton}
            isFullScreenDisabled={isViewStepsEmpty}
            customFullScreenUpperCon={customFullScreenUpperCon}
            assignmentBasicDetails={assignmentBasicDetails}
            showAssignmentActionsButton={showAssignmentActionsButton}
            assignmentData={assignmentData}
            courseData={courseData}
            goToRelativeRoute={goToRelativeRoute}
            isMediaManagerRoute={isMediaManagerRoute}
            totalNewSnpCycles={totalNewSnpCycles}
            isClassStreamManagerRoute={isClassStreamManagerRoute}
            studentAssignmentId={studentAssignmentId}
            goToBack={goToBack}
            courseId={courseId}
            setShouldNavigateToFirstStudentAssignment={
              setShouldNavigateToFirstStudentAssignment
            }
            assessmentType={assessmentType}
            isLinksRoute={isLinksRoute}
            isWorkbookMode={isWorkbookMode}
            {...(isWorkbookMode && { coverImage })}
            canPerformActionsInAssignment={canPerformActionsInAssignment}
            customHeader={customHeader}
            isGradebookRoute={isGradebookRoute}
            isClassroomRoute={isClassroomRoute}
            isClassflowRoute={isClassflowRoute}
            AssignmentActionsDropdown={AssignmentActionsDropdown}
            assignmentId={assignmentId}
            createShortUrl={createShortUrl}
            updateStudentAssignmentLinkShortUrlsMapping={
              updateStudentAssignmentLinkShortUrlsMapping
            }
            studentAssignmentLinkShortUrlsMapping={
              studentAssignmentLinkShortUrlsMapping
            }
            setToastMsg={setToastMsg}
            curriculumProgramId={curriculumProgramId}
            showAssignmentCopyLinkOption={showAssignmentCopyLinkOption}
            learningCourseId={learningCourseId}
            onOrganizationResourceDelete={onOrganizationResourceDelete}
            upperContainerStyle={upperContainerStyle}
            titleStyle={titleStyle}
          />
        ) : null}
        <div className={lowerConClassWrapper} style={lowerConWrapperStyle}>
          {showEmptyState && isViewStepsEmpty ? (
            <EmptyState
              illustration={emptyIllustration}
              title={emptyTitle}
              subtitle={emptySubtitle}
              subtitleStyle={styleObj.emptyStateStyles}
            />
          ) : (
            <div className={lowerConClass}>
              <Steps
                t={t}
                view_steps={viewStepsArray}
                step_list={step_list}
                field_list={field_list}
                assessmentFields={assessmentFields}
                unitPlanFields={unitPlanFields}
                unitPlanAllFields={unitPlanAllFields}
                assessmentId={assessmentId}
                portalType={portalType}
                attachmentItemHeight={attachmentItemHeight}
                assessmentDetails={assessmentDetails}
                curriculumType={curriculumType}
                isFullScreenLE={isFullScreenLE}
                isStudentWorksheetWebView={isStudentWorksheetWebView}
                currentAssessmentTypes={currentAssessmentTypes}
                courseId={courseId}
                isWorkbookMode={isWorkbookMode}
                filterViewSteps={filterViewSteps}
                filterViewFields={filterViewFields}
                customTextViewStyle={customTextViewStyle}
                showPostSettings={showPostSettings}
                isFromAITutorAwards={isFromAITutorAwards}
              />
              {_.size(folders) > 0 &&
              isUserTypeStaff({ userType }) &&
              !isWorkbookMode ? (
                <>
                  <div className={classes.postSettings}>
                    <div className={classes.stepLabel}>
                      {t("common:folders")}
                    </div>
                    {_.map(folders, folder => (
                      <div
                        key={_.get(folder, "id", "")}
                        className={classes.folderListItem}
                      >
                        <FolderOutlined variant="subtle" />
                        <span>{_.get(folder, "name", "")}</span>
                      </div>
                    ))}
                  </div>

                  <div className={classes.separator} />
                </>
              ) : null}
              {showPostSettings &&
              !isWorkbookMode &&
              isUserTypeStaff({ userType }) ? (
                <div className={classes.postSettings}>
                  <div className={classes.stepLabel}>
                    {t("classRoom:post_settings")}
                  </div>
                  {_.map(settingItems, setting => (
                    <div key={setting.key} className={classes.folderListItem}>
                      {setting.value ? (
                        <TickLargeOutlined variant="success" />
                      ) : (
                        <CloseOutlined variant="subtle" />
                      )}
                      <span className={classes.subtleText}>
                        {setting.label}
                      </span>
                    </div>
                  ))}
                </div>
              ) : null}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const renderLowerContainer = () => (
    <div className={lowerConClassWrapper} style={lowerConWrapperStyle}>
      {showEmptyState && isViewStepsEmpty ? (
        <EmptyState
          illustration={emptyIllustration}
          title={emptyTitle}
          subtitle={emptySubtitle}
          subtitleStyle={styleObj.emptyStateStyles}
        />
      ) : (
        <div className={lowerConClass}>
          <Steps
            t={t}
            view_steps={viewStepsArray}
            step_list={step_list}
            field_list={field_list}
            assessmentFields={assessmentFields}
            unitPlanFields={unitPlanFields}
            unitPlanAllFields={unitPlanAllFields}
            assessmentId={assessmentId}
            portalType={portalType}
            attachmentItemHeight={attachmentItemHeight}
            assessmentDetails={assessmentDetails}
            curriculumType={curriculumType}
            isFullScreenLE={isFullScreenLE}
            isStudentWorksheetWebView={isStudentWorksheetWebView}
            currentAssessmentTypes={currentAssessmentTypes}
            courseId={courseId}
            isWorkbookMode={isWorkbookMode}
            filterViewSteps={filterViewSteps}
            filterViewFields={filterViewFields}
            customTextViewStyle={customTextViewStyle}
            showPostSettings={showPostSettings}
            isFromAITutorAwards={isFromAITutorAwards}
          />
        </div>
      )}
    </div>
  );

  const worksheetTabbedView = (
    <div
      className={containerClass}
      onClick={e => e?.stopPropagation()}
      ref={containerRef}
      tabIndex={-1}
    >
      <div className={classes.worksheetInnerContainer}>
        {!isDraftOrScheduledView ? (
          <Header
            t={t}
            LETitle={LETitle}
            createdBy={createdBy}
            onCloseClick={onCloseClick}
            showCloseButton={showCloseButton}
            showCreatedBy={showCreatedBy}
            toggleFullscreenLE={toggleFullscreenLE}
            isFullScreen={isFullScreenLE}
            showFullScreenButton={showFullScreenButton}
            isFullScreenDisabled={isViewStepsEmpty}
            customFullScreenUpperCon={customFullScreenUpperCon}
            assignmentBasicDetails={assignmentBasicDetails}
            showAssignmentActionsButton={showAssignmentActionsButton}
            assignmentData={assignmentData}
            courseData={courseData}
            goToRelativeRoute={goToRelativeRoute}
            isMediaManagerRoute={isMediaManagerRoute}
            totalNewSnpCycles={totalNewSnpCycles}
            isClassStreamManagerRoute={isClassStreamManagerRoute}
            studentAssignmentId={studentAssignmentId}
            goToBack={goToBack}
            courseId={courseId}
            setShouldNavigateToFirstStudentAssignment={
              setShouldNavigateToFirstStudentAssignment
            }
            assessmentType={assessmentType}
            isLinksRoute={isLinksRoute}
            isWorkbookMode={isWorkbookMode}
            {...(isWorkbookMode && { coverImage })}
            canPerformActionsInAssignment={canPerformActionsInAssignment}
            customHeader={customHeader}
            isGradebookRoute={isGradebookRoute}
            isClassroomRoute={isClassroomRoute}
            isClassflowRoute={isClassflowRoute}
            AssignmentActionsDropdown={AssignmentActionsDropdown}
            assignmentId={assignmentId}
            createShortUrl={createShortUrl}
            updateStudentAssignmentLinkShortUrlsMapping={
              updateStudentAssignmentLinkShortUrlsMapping
            }
            studentAssignmentLinkShortUrlsMapping={
              studentAssignmentLinkShortUrlsMapping
            }
            setToastMsg={setToastMsg}
            curriculumProgramId={curriculumProgramId}
            showAssignmentCopyLinkOption={showAssignmentCopyLinkOption}
            learningCourseId={learningCourseId}
            onOrganizationResourceDelete={onOrganizationResourceDelete}
            upperContainerStyle={upperContainerStyle}
            titleStyle={titleStyle}
          />
        ) : null}

        {showStepHeader ? (
          <StepHeader
            steps={WORKSHEET_VIEW_STEPS(t)}
            currentStep={currentWorksheetViewStep}
            onClickStep={onClickWorksheetStep}
          />
        ) : null}

        <div className={classes.worksheetStepsContainer}>
          {currentWorksheetViewStep === "QUESTION_CREATION" ? (
            <Worksheet
              mode={MODE_ASSESSMENT_VIEW}
              parentId={assessmentId}
              parentType={"ASSESSMENT"}
            />
          ) : (
            renderLowerContainer()
          )}
        </div>
      </div>
    </div>
  );

  const componentToRender =
    isWorksheet && !isDraftOrScheduledView
      ? worksheetTabbedView
      : lePreviewComponent;

  return isFullScreenLE && !isDraftOrScheduledView ? (
    <UIModal isOpen={isFullScreenLE} isFullScreen={isFullScreenLE}>
      {componentToRender}
    </UIModal>
  ) : (
    componentToRender
  );
});

CompactLEPreview.displayName = "CompactLEPreview";

const mapActionCreators = {
  getCurrentAssessmentTypes,
  goToRelativeRoute,
  goToBack,
  setShouldNavigateToFirstStudentAssignment,
  createShortUrl,
  updateStudentAssignmentLinkShortUrlsMapping,
  setToastMsg,
};

const mapStateToProps = state => {
  const curriculumType = _.get(state, "platform.currentCurriculumProgram.type");
  const curriculumProgramId = getSelectedCurriculumId(state);
  const organizationId = _.get(state, "login.userInfo.org_id", "");

  const selectedGrades = _.get(
    state,
    "teacher.selected_class.selected_grades",
    []
  );

  const academicYearId = getSelectedAcademicYearId(state);
  const userType = getUserType(state);

  const isUsingNewGradingSetupInCourses = getIsConfigurationEnabledForCPAY({
    configuration: USE_NEW_GRADING_SETUP_IN_COURSE_PERM,
    curriculumProgramId,
    academicYearId: academicYearId,
  });

  const isCustomWeights = getIsCustomWeights({
    curriculumProgramId,
    curriculumProgramType: curriculumType,
  });

  const learningCourseId = getSelectedLearningCourseId(state);

  return {
    userType,
    curriculumType,
    organizationId,
    selectedGrades,
    academicYearId,
    plannerElementSets: _.get(state, "platform.currentPlannerElementSets", []),
    curriculumProgramId,
    isUsingNewGradingSetupInCourses,
    isCustomWeights,
    studentAssignmentLinkShortUrlsMapping: _.get(
      state,
      "classRoom.studentAssignmentLinkShortUrlsMapping",
      {}
    ),
    learningCourseId,
  };
};

const getDerivedProps = ownProps => {
  const {
    organizationId,
    selectedGrades,
    academicYearId,
    courseData,
  } = ownProps;

  // Get grades from Redux state or fallback to courseData grades
  const courseGrades = _.get(courseData, "grades", []);
  const gradesToUse = !_.isNil(selectedGrades) ? selectedGrades : courseGrades;

  const turnitinEnabledGrades = getSettingValue({
    name: "TurnitinEnabledGrades",
    organizationId,
    readFromUserAndOrgLevel: true,
  });

  const isTurnitinEnabled = isTurnitinEnabledForGrade(
    gradesToUse,
    turnitinEnabledGrades
  );

  const userArchivedFilters = {
    academicYearId,
  };
  const collaboratorsFilter = {
    userAcademicYearId: academicYearId,
  };

  return {
    isData: true,
    isLoading: false,
    userArchivedFilters,
    collaboratorsFilter,
    isTurnitinEnabledForGrade: isTurnitinEnabled,
  };
};

const getDynamicTemplate = ({
  template,
  t,
  curriculumType,
  templateId,
  plannerElementSets,
  isCustomWeights,
}) => {
  const dynamicTemplate = updateTemplateForLearningGoals({
    template: _.cloneDeep(template),
    templateId,
  });

  const field_list = dynamicTemplate["field_list"];
  if (_.includes(["UBD"], curriculumType) && !_.isEmpty(plannerElementSets)) {
    updateCustomLabelsForUBDLearningGoals({
      field_list,
      curriculumType,
      plannerElementSets,
      t,
    });
  }

  const localisedTemplate = getLocalisedTemplate({
    template: { body: dynamicTemplate },
    t,
    templateType: "le",
  });

  updateDynamicLabelsForPlannerElements({
    field_list,
    plannerElementSets,
  });

  if (!_.isEmpty(dynamicTemplate)) {
    const step_list = dynamicTemplate["step_list"];

    if (!isCustomWeights) {
      updateStepList({
        step_list,
        fieldsToRemove: ["weightage"],
      });
    }

    if (!getIsEnabledLTITools()) {
      updateStepList({
        step_list,
        fieldsToRemove: ["ltiTemplate"],
      });
    }
  }

  return dynamicTemplate;
};

CompactLEPreview.propTypes = {
  parentType: PropTypes.string,
  isWorkbookMode: PropTypes.bool,
  upperContainerStyle: PropTypes.object,
  titleStyle: PropTypes.object,
  isFromAITutorAwards: PropTypes.bool,
};

CompactLEPreview.defaultProps = {
  parentType: "UNIT_PLAN",
  isFullScreenLE: false,
  isDraftOrScheduledView: false,
  showPostSettings: true,
  isWorkbookMode: false,
  upperContainerStyle: {},
  titleStyle: {},
  isFromAITutorAwards: false,
};

export default compose(
  connect(mapStateToProps, mapActionCreators),
  withDerivedProps(getDerivedProps),
  I18nHOC({ resource: ["unitPlanTemplate", "common", "classRoom"] }),
  graphql(getAssessmentPreviewQuery, {
    name: "getAssessmentPreview",
    options: ({
      assessmentId,
      portalType,
      userArchivedFilters,
      collaboratorsFilter,
    }) => ({
      fetchPolicy: "cache-and-network",
      variables: {
        id: assessmentId,
        portalType,
        userArchivedFilters,
        collaboratorsFilter,
      },
    }),
    props({
      getAssessmentPreview,
      ownProps: {
        isData,
        isLoading,
        isUsingNewGradingSetupInCourses,
        isCustomWeights,
      },
    }) {
      const assessmentDetails = getAssessmentPreviewDetailsFromCache(
        getAssessmentPreview.variables
      );
      const templateId = _.get(assessmentDetails, "templateId", null);
      const unitPlanTemplateId = _.get(
        assessmentDetails,
        "unitPlan.templateId",
        null
      );

      const gradingConfigurations = _.get(
        assessmentDetails,
        "gradingConfigurations",
        []
      );
      const isCustomWeightsV2 =
        isUsingNewGradingSetupInCourses &&
        getIsCustomWeightsV2(gradingConfigurations);

      const assessmentType = _.get(assessmentDetails, "assessmentType.value");
      const taskTypeGroup = _.get(assessmentDetails, "taskType.type");

      return {
        assessmentDetails,
        taskTypeGroup,
        assessmentType,
        unitPlanTemplateId,
        templateId,
        isData: !_.isEmpty(assessmentDetails) && isData,
        isLoading:
          getAssessmentPreview["networkStatus"] == 1 ||
          getAssessmentPreview["networkStatus"] == 2 ||
          isLoading,
        isCustomWeights: isUsingNewGradingSetupInCourses
          ? isCustomWeightsV2
          : isCustomWeights,
      };
    },
  }),
  graphql(getPlannerTemplateQuery, {
    name: "getPlannerTemplate",
    skip: ({ templateId }) => !templateId,
    options: ({ templateId }) => ({
      fetchPolicy: "cache-and-network",
      variables: {
        ids: [templateId],
      },
    }),
    props({
      getPlannerTemplate,
      ownProps: {
        userType,
        isLoading,
        isData,
        templateId,
        assessmentDetails,
        parentType,
        assessmentType,
        taskTypeGroup,
        curriculumType,
        t,
        plannerElementSets,
        courseData,
        isWorkbookMode,
        isCustomWeights,
      },
    }) {
      const unitPlanFields = getUnitPlanFieldsFromAssessment({
        assessmentDetails,
      });

      const unitPlanAllFields = _.get(
        assessmentDetails,
        "unitPlan.allFields",
        []
      );

      const assessmentFields = _.get(assessmentDetails, "allFields", []);

      const templates = getPlannerTemplateFromCache(
        getPlannerTemplate.variables,
        true
      );
      const templateFromQuery = _.get(
        _.find(templates, { id: templateId }),
        "body",
        {}
      );

      const originalTemplate = getDynamicTemplate({
        template: templateFromQuery,
        assessmentFields,
        t,
        curriculumType,
        templateId,
        plannerElementSets,
        isCustomWeights,
      });

      const template = updateAssessmentTemplate({
        t,
        template: originalTemplate,
        assessmentType,
        taskTypeGroup,
        isFromClassroom: true,
        curriculumType,
        resourceFields: assessmentFields,
        mode: "view",
        plannerElementSets,
        subjects: _.get(courseData, "subjects", []),
        templateId,
        userType,
        assessmentTools: _.get(assessmentDetails, "assessmentTools", []),
      });

      const listSteps = GetLESteps({
        userType,
        template,
        resourceDetails: assessmentDetails,
        unitPlanFields,
        parentType,
        isWorkbookMode,
        curriculumProgramType: curriculumType,
      });

      return {
        step_list: listSteps.stepList,
        view_steps: listSteps.viewSteps,
        field_list: _.get(template, "field_list", {}),
        assessmentFields,
        unitPlanFields: combineUnitPlanMultiFieldData({
          templateBody: templateFromQuery,
          unitPlanFields: _.cloneDeep(unitPlanFields),
        }),
        unitPlanAllFields,
        isData: !_.isEmpty(templates) && isData,
        isLoading:
          getPlannerTemplate["networkStatus"] == 1 ||
          getPlannerTemplate["networkStatus"] == 2 ||
          isLoading,
      };
    },
  }),
  graphql(getAssignmentFoldersAndPostSettingsQuery, {
    name: "getAssignmentFoldersAndPostSettings",
    skip: ({ assignmentId }) => !assignmentId,
    options: ({ assignmentId }) => ({
      fetchPolicy: "cache-and-network",
      variables: {
        id: assignmentId,
      },
    }),
    props: ({
      getAssignmentFoldersAndPostSettings,
      ownProps: { assignmentId, isData, isLoading },
    }) => {
      const assignmentBasicDetails = getAssignmentFoldersAndPostSettingsFromCache(
        assignmentId
      );

      return {
        assignmentBasicDetails,
        isData: !_.isEmpty(assignmentBasicDetails) && isData,
        isLoading:
          getAssignmentFoldersAndPostSettings["networkStatus"] == 1 ||
          getAssignmentFoldersAndPostSettings["networkStatus"] == 2 ||
          isLoading,
      };
    },
  }),
  withLoader
)(CompactLEPreview);
